import React, { useState, useEffect } from 'react';
import {
    Form,
    Card,
    Button,
    Row,
    Col,
    Input,
    Select,
    Switch,
    message,
    Collapse,
    Divider,
    Tag
} from 'antd';
import {
    UserOutlined,
    LeftOutlined,
    SaveOutlined,
    TeamOutlined,
    LockOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import * as adminactions from "store/actions/AuthActions";

const { Option } = Select;
const { Panel } = Collapse;

const UserForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [roles, setRoles] = useState([]);
    const [permissions, setPermissions] = useState([]);

    useEffect(() => {
        loadData();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadData = async () => {
        setPageLoading(true);
        try {
            const [rolesRes, permissionsRes] = await Promise.all([
                adminactions.loadRoles(),
                adminactions.loadPermissions()
            ]);

            setRoles(Array.isArray(rolesRes) ? rolesRes : rolesRes?.results || []);
            setPermissions(Array.isArray(permissionsRes) ? permissionsRes : permissionsRes?.results || []);

            if (id) {
                const userResponse = await adminactions.fetchUsers(id);
                if (userResponse && !userResponse.message) {
                    setUser(userResponse);

                    // Set form values
                    form.setFieldsValue({
                        username: userResponse.username,
                        first_name: userResponse.first_name,
                        middle_name: userResponse.middle_name,
                        last_name: userResponse.last_name,
                        email: userResponse.email,
                        mobile: userResponse.mobile,
                        is_staff: userResponse.is_staff,
                        is_active: userResponse.is_active,
                        is_2fa_enabled: userResponse.is_2fa_enabled,
                        roles: userResponse.roles?.map(r => r.id) || [],
                        permissions: userResponse.permissions?.map(p => p.id) || []
                    });
                }
            }
        } catch (error) {
            console.error('Error loading data:', error);
            message.error('Failed to load data');
        } finally {
            setPageLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            if (id) {
                const response = await adminactions.updateUsers(values, id);
                if (response && !response.message) {
                    message.success('User updated successfully!');
                    navigate("/users");
                } else {
                    message.error(response?.message || 'Failed to update user');
                }
            } else {
                const response = await adminactions.addUsers(values);
                if (response && !response.message) {
                    message.success('User created successfully!');
                    navigate("/users");
                } else {
                    message.error(response?.message || 'Failed to create user');
                }
            }
        } catch (error) {
            message.error('Failed to save user');
            console.error('Save error:', error);
        } finally {
            setLoading(false);
        }
    };

    if (pageLoading) {
        return (
            <div style={{
                height: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <div>Loading...</div>
            </div>
        );
    }

    return (
        <div style={{
            padding: '20px',
            paddingBottom: '60px',
            height: '100vh',
            overflow: 'auto'
        }}>
            <Card
                title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Button
                            type="link"
                            icon={<LeftOutlined />}
                            onClick={() => navigate('/users')}
                            style={{ paddingLeft: 0, marginRight: 8 }}
                        >
                            Back
                        </Button>
                        <span>{id ? `Edit User - ${user?.first_name || ''} ${user?.last_name || ''}` : 'Add New User'}</span>
                    </div>
                }
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    size="large"
                >
                    <Collapse defaultActiveKey={['1', '2', '3']} style={{ marginBottom: 24 }}>
                        {/* Basic Information */}
                        <Panel
                            header={
                                <span>
                                    <UserOutlined style={{ marginRight: 8 }} />
                                    Basic Information
                                </span>
                            }
                            key="1"
                        >
                            <Card style={{ margin: 0, border: 'none', boxShadow: 'none' }}>
                                <Row gutter={[24, 16]}>
                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item
                                            label="Username"
                                            name="username"
                                            rules={[
                                                { required: true, message: 'Please enter username!' },
                                                { min: 3, message: 'Username must be at least 3 characters!' }
                                            ]}
                                        >
                                            <Input placeholder="Enter username" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item
                                            label="Email"
                                            name="email"
                                            rules={[
                                                { required: true, message: 'Please enter email!' },
                                                { type: 'email', message: 'Please enter a valid email!' }
                                            ]}
                                        >
                                            <Input placeholder="Enter email address" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item
                                            label="Mobile"
                                            name="mobile"
                                        >
                                            <Input placeholder="Enter mobile number" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item
                                            label="First Name"
                                            name="first_name"
                                        >
                                            <Input placeholder="Enter first name" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item
                                            label="Middle Name"
                                            name="middle_name"
                                        >
                                            <Input placeholder="Enter middle name (optional)" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item
                                            label="Last Name"
                                            name="last_name"
                                        >
                                            <Input placeholder="Enter last name" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        </Panel>

                        {/* Roles & Permissions */}
                        <Panel
                            header={
                                <span>
                                    <TeamOutlined style={{ marginRight: 8 }} />
                                    Roles & Permissions
                                </span>
                            }
                            key="2"
                        >
                            <Card style={{ margin: 0, border: 'none', boxShadow: 'none' }}>
                                <Row gutter={[24, 16]}>
                                    <Col xs={24}>
                                        <Form.Item
                                            label="Roles"
                                            name="roles"
                                            help="Assign roles to the user. Roles contain predefined sets of permissions."
                                        >
                                            <Select
                                                mode="multiple"
                                                placeholder="Select roles"
                                                optionFilterProp="children"
                                                showSearch
                                                allowClear
                                                filterOption={(input, option) =>
                                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                                }
                                            >
                                                {roles.map(role => (
                                                    <Option key={role.id} value={role.id}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <span>{role.role_name}</span>
                                                            {role.status === 'ACTIVE' && (
                                                                <Tag color="green" style={{ marginLeft: 8 }}>Active</Tag>
                                                            )}
                                                        </div>
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24}>
                                        <Divider orientation="left" style={{ margin: '12px 0' }}>Additional Permissions</Divider>
                                    </Col>
                                    <Col xs={24}>
                                        <Form.Item
                                            label="Permissions"
                                            name="permissions"
                                            help="Select additional permissions for this user beyond their role permissions."
                                        >
                                            <Select
                                                mode="multiple"
                                                placeholder="Select additional permissions"
                                                optionFilterProp="children"
                                                showSearch
                                                allowClear
                                                maxTagCount="responsive"
                                                filterOption={(input, option) =>
                                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                                }
                                            >
                                                {permissions.map(permission => (
                                                    <Option key={permission.id} value={permission.id}>
                                                        {permission.permission}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        </Panel>

                        {/* Security & Status */}
                        <Panel
                            header={
                                <span>
                                    <LockOutlined style={{ marginRight: 8 }} />
                                    Security & Status
                                </span>
                            }
                            key="3"
                        >
                            <Card style={{ margin: 0, border: 'none', boxShadow: 'none' }}>
                                <Row gutter={[24, 16]}>
                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item
                                            label="Two-Factor Authentication"
                                            name="is_2fa_enabled"
                                            valuePropName="checked"
                                        >
                                            <Switch
                                                checkedChildren="Enabled"
                                                unCheckedChildren="Disabled"
                                            />
                                        </Form.Item>
                                        <p style={{ color: '#666', fontSize: '12px', marginTop: '-12px' }}>
                                            Enable 2FA for enhanced security
                                        </p>
                                    </Col>
                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item
                                            label="Staff Status"
                                            name="is_staff"
                                            valuePropName="checked"
                                        >
                                            <Switch
                                                checkedChildren="Staff"
                                                unCheckedChildren="Not Staff"
                                            />
                                        </Form.Item>
                                        <p style={{ color: '#666', fontSize: '12px', marginTop: '-12px' }}>
                                            Staff users can access admin panel
                                        </p>
                                    </Col>
                                    <Col xs={24} sm={12} md={8}>
                                        <Form.Item
                                            label="Active Status"
                                            name="is_active"
                                            valuePropName="checked"
                                            initialValue={true}
                                        >
                                            <Switch
                                                checkedChildren="Active"
                                                unCheckedChildren="Inactive"
                                            />
                                        </Form.Item>
                                        <p style={{ color: '#666', fontSize: '12px', marginTop: '-12px' }}>
                                            Inactive users cannot log in
                                        </p>
                                    </Col>
                                </Row>
                            </Card>
                        </Panel>
                    </Collapse>

                    <div style={{ textAlign: 'right', paddingTop: 16, marginBottom: 40 }}>
                        <Button
                            style={{ marginRight: 8 }}
                            size="large"
                            onClick={() => navigate('/users')}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            htmlType="submit"
                            loading={loading}
                            size="large"
                        >
                            {id ? 'Update User' : 'Create User'}
                        </Button>
                    </div>
                </Form>
            </Card>
        </div>
    );
};

export default UserForm;
